import axios, { capi, extractData, invoiceApi, parentAccountApi } from '@atheneum/api'
import { formatDate, formatDateToDate } from '@atheneum/api/dist/lib'
import { action, computed, extendObservable } from 'mobx'

class InvoiceStore {
  constructor(growlStore, staticData) {
    extendObservable(this, {
      growlStore,
      error: null,
      loading: false,
      invoices: [],
      loadingSingle: false,
      invoice: null,
      invoicingEntitiesLoading: false,
      invoicingEntities: [],
      attachments: null,
      formData: null,
      isCapiInvoice: false,
      isCapiCallOnlyInvoice: false,
      isFreeCallInvoice: false,
      isPayg: computed(() => {
        if (!this.invoice) return false
        return (
          this.invoice.invoiceTypeId === staticData.CONST.INVOICE_TYPES.PAYG
        )
      }),
      isNonPayg: computed(() => {
        if (!this.invoice) return false
        return (
          this.invoice.invoiceTypeId === staticData.CONST.INVOICE_TYPES.NON_PAYG
        )
      }),

      // this will present invoice data,
      // if the invoice is in status < Created then directly from invoice and relevant tables
      // otherwise from invoice.invoiceImmutableData
      invoiceData: computed(() => {
        if (!this.invoice) return null

        return this.buildInvoiceData(staticData, this.invoice)
      })
    })
  }

  getInvoicingEntities = async ({ parentAccountId, accountId }) => {
    this.invoicingEntitiesLoading = true
    try {
      const invoicingEntities = await parentAccountApi.getInvoicingEntities(
        parentAccountId,
        accountId
      )

      this.invoicingEntities.replace(invoicingEntities)
      this.invoicingEntitiesLoading = false
    } catch (error) {
      this.error = error
      this.invoicingEntities.replace([])
      this.invoicingEntitiesLoading = false
    }
  }

  // @TODO: Remove this when all references are swapped with getListByProject
  getList = action(projectId => {
    return this.getListByProject(projectId)
  })

  getListByProject = action(projectId => {
    this.loading = true

    return invoiceApi
      .getProjectInvoices(projectId)
      .then(async invoices => {
        const formattedInvoices = await Promise.all(invoices.map(async invoice => {
          try {
            const capiInvoice = await capi.checkInvoice({
              invoiceId: invoice.id
            })

            return {
              ...invoice,
              isENSInvoice: !!capiInvoice,
              isCapiCallOnlyInvoice: capiInvoice ? capiInvoice.call_only_invoice : false,
              isFreeCallInvoice: capiInvoice ? capiInvoice.is_free_call_invoice : false,
            }
          } catch (error) {
            return invoice
          }
        }))
        this.invoices.replace(formattedInvoices)
        this.loading = false
      })
      .catch(error => {
        this.loading = false
        this.error = error
      })
  })

  getListByContract = action(contractId => {
    this.loading = true

    return invoiceApi
      .getContractInvoices(contractId)
      .then(invoices => {
        this.invoices.replace(invoices)
        this.loading = false
      })
      .catch(error => {
        console.error(error)
        this.loading = false
        this.error = error
      })
  })

  get = action(id => {
    this.loadingSingle = true

    return invoiceApi
      .getInvoice(id)
      .then(invoice => {
        this.invoice = invoice
        this.loadingSingle = false
        return invoice
      })
      .catch(error => {
        console.error(error)
        this.error = error
        this.loadingSingle = false
      })
  })

  clearInvoice = action(() => {
    this.invoice = null
    this.loadingSingle = false
  })

  delete = action(id => {
    this.loadingSingle = true
    return invoiceApi
      .deleteInvoice(id)
      .then(response => {
        this.loadingSingle = false
        this.growlStore.showSuccess('You have successfully deleted invoice.')
      })
      .catch(error => {
        console.error(error)
        this.loadingSingle = false
        throw error
      })
  })

  updateToRequestApprovalStatus = invoice => {
    return invoiceApi.requestInvoiceApproval(invoice.id)
  }

  updateToApproveStatus = (invoice) => {
    return invoiceApi.approveInvoice(invoice.id)
  }

  updateToDisapproveStatus = (invoice, disapprovedReason) => {
    return invoiceApi.disapproveInvoice(invoice.id, disapprovedReason)
  }

  updateToCreatedStatus = (invoice, data) => {
    return invoiceApi.setCreatedStatus(invoice.id, data)
  }

  createCreditNote = (invoice, creditNoteData) => {
    return invoiceApi.createCreditNote(invoice.id, creditNoteData)
  }

  updateCreditNoteToApprovedStatus = (invoice, invoiceDates) => {
    return invoiceApi.setCreditNoteToApprovedStatus(invoice.id, invoiceDates)
  }

  removeInvoiceItem = (invoiceId, invoiceItemId) => {
    return invoiceApi
      .deleteInvoiceItem(invoiceItemId)
      .then(() => {
        this.get(invoiceId)
        this.growlStore.showSuccess(
          'You have successfully removed the invoice item from invoice.'
        )
      })
      .catch(error => {
        this.loading = false
        this.growlStore.showError(error)
      })
  }

  removeInvoicePayment = (invoiceId, invoicePaymentId) => {
    return invoiceApi
      .deleteInvoicePayment(invoiceId, invoicePaymentId)
      .then(() => {
        this.get(invoiceId)
        this.growlStore.showSuccess(
          'You have successfully removed the invoice payment from invoice.'
        )
      })
      .catch(error => {
        this.loading = false
        this.growlStore.showError(error)
      })
  }

  removeFeeFromInvoice = feeId => {
    return invoiceApi
      .deleteFeeFromInvoice(this.invoice.id, feeId)
      .then(() => {
        this.get(this.invoice.id)
        this.growlStore.showSuccess(
          'You have successfully removed fee from invoice.'
        )
      })
      .catch(error => {
        this.loading = false
        this.error = error
        this.growlStore.showError(error)
      })
  }

  getAttachments = id => {
    return axios.get('/api/invoice/' + id + '/attachments').then(res => {
      this.attachments = extractData(res)
    })
  }

  deleteAttachment = id => {
    return axios.delete('/api/invoice/attachments/' + id).then(res => {
      this.getAttachments(this.invoice.id)
    })
  }

  getCapiInvoice = async (invoiceId) => {
    const capiInvoice = await capi.checkInvoice({
      invoiceId
    })
    
    if (capiInvoice &&
      capiInvoice.platform_invoice_id) {
      this.capiInvoiceStatus = capiInvoice.status
      this.isCapiInvoice = invoiceId === parseInt(capiInvoice.platform_invoice_id)
      this.isCapiCallOnlyInvoice = capiInvoice.call_only_invoice
      this.isFreeCallInvoice = capiInvoice.is_free_call_invoice
    } else {
      this.capiInvoiceStatus = null
      this.isCapiInvoice = false
      this.isCapiCallOnlyInvoice = false
      this.isFreeCallInvoice = false
    }
  }

  /**
   * Builds reading data, depending on the status:
   *
   * if < CREATED
   *  then from relations
   * else
   *  from immutable data
   */
  buildInvoiceData = (staticData, invoice) => {
    const { CONST, names } = staticData
    const { INVOICE_STATUSES } = CONST

    let invoiceData = {
      name: invoice.name || '',
      draftName: invoice.draftName || '',
      invoiceTypeId: invoice.invoiceTypeId,
      invoiceType: names.invoiceTypes[invoice.invoiceTypeId],
      invoiceStatusId: invoice.invoiceStatusId,
      invoiceStatus: names.invoiceStatuses[invoice.invoiceTypeId],
      vat: invoice.vat,
      caseCode: invoice.caseCode,
      currency: names.currencies[invoice.currencyId] || 'N/A',
      sentDate: formatDate(invoice.sentDate),
      dueDate: formatDate(invoice.dueDate),
      documentDate: formatDate(invoice.documentDate),
      approvedBy: invoice.approvedBy ? invoice.approvedBy.fullName : '',
      approvedDate: formatDate(invoice.approvedDate),
      disapprovedBy: invoice.disapprovedBy
        ? invoice.disapprovedBy.fullName
        : '',
      disapprovedDate: formatDate(invoice.disapprovedDate),
      disapprovedReason: invoice.disapprovedReason,
      comment: invoice.comment,
      internalRemarks: invoice.internalRemarks,
      isWriteOff: invoice.isWriteOff
    }

    if (invoice.invoiceStatusId >= INVOICE_STATUSES.CREATED) {
      const immutableData = invoice.invoiceImmutableData
      invoiceData = {
        ...invoiceData,
        invoicingEntity: {
          legalName: immutableData.invoicingEntityLegalName
        },
        atheneumOffice: {
          name: immutableData.atheneumOfficeName
        },
        periodOfService: immutableData.periodOfService
      }
    } else {
      invoiceData = {
        ...invoiceData,
        invoicingEntity: {
          legalName: invoice.invoicingEntity
            ? invoice.invoicingEntity.legalName
            : ''
        },
        atheneumOffice: {
          name: invoice.atheneumOffice ? invoice.atheneumOffice.name : ''
        },
        periodOfService: `${formatDateToDate(
          invoice.startDate,
          invoice.endDate
        )} `
      }
    }

    return invoiceData
  }
}

export default InvoiceStore
